/**
 * use-requests.ts
 * 
 * Hook personalizzato per la gestione delle richieste.
 * Utilizza TanStack Query per caching e sincronizzazione dello stato server.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requestsNew } from '../services/api';
import { useCacheConfig } from '../contexts/cache-config-context';
import { 
  Request, 
  RequestCreateRequest, 
  RequestUpdateRequest, 
  RequestStatus,
  RequestType, 
  SearchParams 
} from '../types/api';

export function useRequests(params?: SearchParams) {
  const { config } = useCacheConfig();
  const queryClient = useQueryClient();

  const getAllQuery = useQuery({
    queryKey: ['requests', params],
    queryFn: () => requestsNew.getAllRequests(params),
    staleTime: config.requests,
    gcTime: config.requests * 2,
  });

  const createMutation = useMutation({
    mutationFn: (data: RequestCreateRequest) => requestsNew.createRequest(data),
    onSuccess: (newRequest) => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.setQueryData(['requests', (newRequest as Request).id], newRequest);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: RequestUpdateRequest }) => 
      requestsNew.updateRequest(id, data),
    onSuccess: (updatedRequest) => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.setQueryData(['requests', (updatedRequest as Request).id], updatedRequest);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => requestsNew.deleteRequest(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.removeQueries({ queryKey: ['requests', deletedId] });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, notes, completedAt }: { id: string; status: RequestStatus; notes?: string; completedAt?: string }) =>
      requestsNew.updateRequestStatus(id, status, notes, completedAt),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['requests', id] });
    },
  });
  return {
    // Queries
    requests: getAllQuery.data,
    isLoading: getAllQuery.isLoading,
    error: getAllQuery.error,
    isError: getAllQuery.isError,
    refetch: getAllQuery.refetch,
    
    // Mutations
    createMutation,
    updateMutation,
    deleteMutation,
    updateStatusMutation,
    
    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isUpdatingStatus: updateStatusMutation.isPending,
  };
}

export function useRequest(id: string) {
  const { config } = useCacheConfig();
  const queryClient = useQueryClient();

  const requestQuery = useQuery({
    queryKey: ['requests', id],
    queryFn: () => requestsNew.getRequestById(id),
    enabled: !!id,
    staleTime: config.requests,
    gcTime: config.requests * 2,
  });

  const updateMutation = useMutation({
    mutationFn: (data: RequestUpdateRequest) => requestsNew.updateRequest(id, data),
    onSuccess: (updatedRequest) => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.setQueryData(['requests', id], updatedRequest);
    },
  });

  return {
    request: requestQuery.data,
    isLoading: requestQuery.isLoading,
    error: requestQuery.error,
    isError: requestQuery.isError,
    refetch: requestQuery.refetch,
    
    // Mutations
    updateMutation,
    
    // Mutation states
    isUpdating: updateMutation.isPending,
  };
}

export function useRequestsByStatus(status: RequestStatus) {
  const { config } = useCacheConfig();

  return useQuery({
    queryKey: ['requests', 'status', status],
    queryFn: () => requestsNew.getRequestsByStatus(status),
    staleTime: config.requests,
    gcTime: config.requests * 2,
  });
}

export function useRequestsByClient(clientId: string) {
  const { config } = useCacheConfig();

  return useQuery({
    queryKey: ['requests', 'client', clientId],
    queryFn: () => requestsNew.getRequestsByClient(clientId),
    enabled: !!clientId,
    staleTime: config.requests,
    gcTime: config.requests * 2,
  });
}

export function useRequestSearch(searchTerm?: string, filters?: {
  status?: RequestStatus;
  type?: RequestType;
  clientId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}) {
  const { config } = useCacheConfig();

  return useQuery({
    queryKey: ['requests', 'search', searchTerm, filters],
    queryFn: () => requestsNew.searchRequests({ searchTerm, ...filters }),
    enabled: (searchTerm && searchTerm.length > 2) || !!filters,
    staleTime: config.search,
    gcTime: config.search * 2,
  });
}

export function useRequestsByType(
  type: RequestType,
  params?: {
    pageNumber?: number;
    pageSize?: number;
    searchTerm?: string;
    progressiveYear?: number;
    status?: RequestStatus;
    startDate?: string;
    endDate?: string;
    /** Filtro su più stati insieme (es. pagina Pre-Campionatura), in alternativa a `status` */
    statuses?: RequestStatus[];
  }
) {
  const { config } = useCacheConfig();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['requests', 'type', type, params],
    queryFn: () => requestsNew.getRequestsByType(type, params),
    staleTime: config.requests,
    gcTime: config.requests * 2,
    placeholderData: (prev) => prev,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['requests', 'type', type] });
  };

  return {
    data: query.data,
    items: query.data?.items || [],
    totalCount: query.data?.totalCount || 0,
    pageNumber: query.data?.pageNumber || 1,
    pageSize: query.data?.pageSize || 20,
    totalPages: query.data?.totalPages || 1,
    hasNextPage: query.data?.hasNextPage || false,
    hasPreviousPage: query.data?.hasPreviousPage || false,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    isError: query.isError,
    refetch: query.refetch,
    invalidate,
  };
}
